import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/jwt-auth-client";
import { useOrganizationStore } from "@/store/useOrganizationStore";

export interface WorkspaceRecord {
  id: string;
  name: string;
  slug: string;
  description: string;
  brand_id: string | null;
  user_id: string | null;
  logo_artifact: string | null;
  logo_storage_key: string | null;
  logo_image_url: string | null;
  metadata: Record<string, unknown> | null;
  session_count: number;
  artifact_count: number;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceDetail extends Omit<WorkspaceRecord, "session_count" | "artifact_count"> {
  sessions: Array<{
    id: string;
    name: string;
    slug: string;
    status: string;
    task_type: string;
    workflow_slug: string;
    brand_id: string | null;
    created_at: string;
  }>;
}

export interface CreateWorkspacePayload {
  name: string;
  slug: string;
  description?: string;
  brand_id?: string | null;
  metadata?: Record<string, unknown>;
  organization?: string;
}

// Stable empty-array reference. Returning `query.data ?? []` would mint a
// fresh array on every render and re-trigger consumer useEffects/useMemos.
const EMPTY_WORKSPACES: WorkspaceRecord[] = [];

/** Query keys for the workspaces API. */
export const workspacesKeys = {
  all: ["workspaces"] as const,
  list: (organizationId: string | null) =>
    ["workspaces", "list", organizationId] as const,
  detail: (idOrSlug: string | undefined) =>
    ["workspaces", "detail", idOrSlug ?? null] as const,
};

async function fetchWorkspacesList(
  organizationId: string | null,
  signal?: AbortSignal,
): Promise<WorkspaceRecord[]> {
  const url = organizationId
    ? `/api/workspaces/?organization_id=${organizationId}`
    : "/api/workspaces/";
  const res = await apiFetch(url, { signal });
  if (!res.ok) throw new Error("Failed to fetch workspaces");
  const data = (await res.json()) as unknown;
  return Array.isArray(data) ? (data as WorkspaceRecord[]) : [];
}

async function fetchWorkspaceDetail(
  idOrSlug: string,
  signal?: AbortSignal,
): Promise<WorkspaceDetail> {
  // Try ID-based lookup first, fall back to slug-based.
  let res = await apiFetch(`/api/workspaces/${idOrSlug}/`, { signal });
  if (!res.ok) {
    res = await apiFetch(`/api/workspaces/by-slug/${encodeURIComponent(idOrSlug)}/`, {
      signal,
    });
  }
  if (!res.ok) throw new Error("Workspace not found");
  return (await res.json()) as WorkspaceDetail;
}

async function createWorkspaceRequest(
  payload: CreateWorkspacePayload,
): Promise<WorkspaceRecord> {
  const res = await apiFetch("/api/workspaces/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error || "Failed to create workspace");
  }
  return (await res.json()) as WorkspaceRecord;
}

async function deleteWorkspaceRequest(id: string): Promise<void> {
  const res = await apiFetch(`/api/workspaces/${id}/`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete workspace");
}

export function useWorkspaces() {
  const qc = useQueryClient();
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId);

  const query = useQuery({
    queryKey: workspacesKeys.list(activeOrganizationId ?? null),
    queryFn: ({ signal }) => fetchWorkspacesList(activeOrganizationId ?? null, signal),
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateWorkspacePayload) =>
      createWorkspaceRequest({
        ...payload,
        organization:
          payload.organization ?? activeOrganizationId ?? undefined,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: workspacesKeys.all }),
  });

  // Optimistic delete: drop the row from the cache immediately, restore on
  // failure. Mirrors the prior local-state filter.
  const deleteMutation = useMutation({
    mutationFn: deleteWorkspaceRequest,
    onMutate: async (id: string) => {
      const queryKey = workspacesKeys.list(activeOrganizationId ?? null);
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<WorkspaceRecord[]>(queryKey);
      if (prev) {
        qc.setQueryData<WorkspaceRecord[]>(
          queryKey,
          prev.filter((w) => w.id !== id),
        );
      }
      return { prev, queryKey };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev && ctx.queryKey) {
        qc.setQueryData(ctx.queryKey, ctx.prev);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: workspacesKeys.all }),
  });

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({
      queryKey: workspacesKeys.list(activeOrganizationId ?? null),
    });
  }, [qc, activeOrganizationId]);

  const createWorkspace = useCallback(
    async (payload: CreateWorkspacePayload): Promise<WorkspaceRecord | null> => {
      try {
        return await createMutation.mutateAsync(payload);
      } catch {
        return null;
      }
    },
    [createMutation],
  );

  const deleteWorkspace = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        await deleteMutation.mutateAsync(id);
        return true;
      } catch {
        return false;
      }
    },
    [deleteMutation],
  );

  const errorSource =
    query.error ?? createMutation.error ?? deleteMutation.error ?? null;

  return {
    workspaces: query.data ?? EMPTY_WORKSPACES,
    loading: query.isLoading,
    error: errorSource
      ? errorSource instanceof Error
        ? errorSource.message
        : String(errorSource)
      : null,
    refresh,
    createWorkspace,
    deleteWorkspace,
  };
}

export function useWorkspaceDetail(workspaceIdOrSlug: string | undefined) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: workspacesKeys.detail(workspaceIdOrSlug),
    queryFn: ({ signal }) => fetchWorkspaceDetail(workspaceIdOrSlug!, signal),
    enabled: !!workspaceIdOrSlug,
  });

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({
      queryKey: workspacesKeys.detail(workspaceIdOrSlug),
    });
  }, [qc, workspaceIdOrSlug]);

  return {
    workspace: query.data ?? null,
    loading: query.isLoading,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : String(query.error)
      : null,
    refresh,
  };
}
